import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { MobileTimePicker as MuiTimePicker } from '@mui/x-date-pickers-pro';
import { useInputGroup } from '../../Hooks/useInputGroup';
import { timeToStr, tryStrToTime } from '../../Utils/stringUtils';
import {
  TimePickerTextEdit,
  ITimePickerTextEditProps,
} from './Components/TimePickerTextEdit';
import { IEditComponentProps } from '../../Components/Attributes/EditableAttribute/EditComponentTypes';

interface IProps extends IEditComponentProps<any> {
  onTimeChange?: (newValue: string, isValidDate: boolean) => void;
  eventOnChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onTimeBlur?: (
    event: React.FocusEvent<HTMLInputElement>,
    newValue: string,
    isValidDate: boolean
  ) => void;
  fullscreenStillRenderTextEdit?: boolean;
  noChangeOnBlur?: boolean;
  noParsing?: boolean;
  parseParams?: string;
}

export const EDTimePicker: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const {
    value,
    onBlur,
    onTimeBlur,
    noChangeOnBlur,
    disableFutureDates,
    onChange,
    onTimeChange,
    ...textProps
  } = props;

  const [timeOpen, setTimeOpen] = useState(false);

  const handleChange = useCallback(
    (newValue: string, isValidDate: boolean) => {
      onChange?.(newValue);
      onTimeChange?.(newValue, isValidDate);
    },
    [onChange, onTimeChange]
  );

  const {
    onFocus,
    ref: fullscreenInputRef,
    active: inputGroupActive,
    navigateNext: inputGroupGoNext,
  } = useInputGroup(props.onFocus, props.disabled);

  const textFieldProps: ITimePickerTextEditProps = {
    editValue: value,
    editOnChange: handleChange,
    noChangeOnBlur: noChangeOnBlur,
    editOnBlur: onBlur,
    editOnFocus: onFocus,
    textEditProps: textProps,
    buttonOnClick: () => {
      setTimeOpen(!timeOpen);
    },
    inputGroupActive: inputGroupActive,
    inputGroupGoNext: inputGroupGoNext,
    inputGroupRef: fullscreenInputRef,
  };

  const timePicker = (
    <MuiTimePicker
      slots={{
        textField: TimePickerTextEdit,
      }}
      slotProps={{
        toolbar: { hidden: true },
        textField: textFieldProps as any,
      }}
      enableAccessibleFieldDOMStructure={false}
      value={tryStrToTime(value ?? '') ?? null}
      open={timeOpen}
      onChange={time => {
        if (props.onChange) {
          if (time != null && typeof time === 'object') {
            onChange?.(timeToStr(time));
            onTimeChange?.(timeToStr(time), true);
          } else {
            onChange?.('');
            onTimeChange?.('', false);
          }
        }
      }}
      onClose={() => {
        setTimeOpen(false);
      }}
    />
  );

  return (
    <>
      {(!inputGroupActive || props.fullscreenStillRenderTextEdit) && timePicker}
      {fullscreenInputRef &&
        inputGroupActive &&
        createPortal(timePicker, fullscreenInputRef)}
    </>
  );
};
